
import { AlignLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import React from "react";

interface LogoIconProps {
  className?: string;
  iconClassName?: string;
  textClassName?: string;
  size?: 'small' | 'medium-small' | 'medium' | 'large';
  variant?: 'default' | 'landing' | 'darkGray';
  children?: React.ReactNode;
}

export const LogoIcon = ({
  className,
  iconClassName,
  textClassName,
  size = 'medium', 
  variant = 'default',
  children
}: LogoIconProps) => {
  // Set icon size based on the size prop
  const iconSize = (() => {
    switch (size) {
      case 'small': return 'h-4 w-4';
      case 'medium-small': return 'h-5 w-5';
      case 'large': return 'h-8 w-8';
      default: return 'h-6 w-6';
    }
  })();
  
  // Define color classes based on variant
  const getIconClass = () => {
    switch (variant) {
      case 'landing': return 'text-white';
      case 'darkGray': return 'text-gray-800 dark:text-white';
      default: return 'text-indigo-600 dark:text-white';
    }
  };
  
  const getTextClass = () => {
    switch (variant) {
      case 'landing': return 'text-white';
      case 'darkGray': return 'text-gray-800 dark:text-white';
      default: return 'text-indigo-600 dark:text-white';
    }
  };
  
  const defaultIconClass = getIconClass();
  const defaultTextClass = getTextClass();
  
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <AlignLeft className={cn(iconSize, defaultIconClass, iconClassName)} />
      {children && <h1 className={cn("font-bold", defaultTextClass, textClassName)}>{children}</h1>}
    </div>
  );
};

interface LogoProps {
  className?: string;
  iconClassName?: string;
  textClassName?: string;
  showText?: boolean;
  showTagline?: boolean;
  size?: 'small' | 'medium-small' | 'medium' | 'large';
  variant?: 'default' | 'landing' | 'darkGray';
}

export const Logo = ({
  className,
  iconClassName,
  textClassName,
  showText = true,
  showTagline = true,
  size = 'medium', 
  variant = 'default'
}: LogoProps) => {
  // Set text size based on the size prop
  const textSize = (() => {
    switch (size) {
      case 'small': return 'text-lg';
      case 'medium-small': return 'text-xl';
      case 'large': return 'text-3xl';
      default: return 'text-2xl';
    }
  })();
  const taglineSize = (() => {
    switch (size) {
      case 'small': return 'text-xs';
      case 'medium-small': return 'text-sm';
      case 'large': return 'text-base';
      default: return 'text-sm';
    }
  })();
  
  // Define tagline color class based on variant
  const getTaglineClass = () => {
    switch (variant) {
      case 'landing': return 'text-white/80';
      case 'darkGray': return 'text-gray-600 dark:text-white/80';
      default: return 'text-indigo-600 dark:text-white/80';
    }
  };
  
  const taglineClass = getTaglineClass();
  
  return (
    <div className={cn("flex flex-col items-center", className)}>
      <LogoIcon 
        iconClassName={iconClassName}
        textClassName={cn(textSize, textClassName)}
        size={size}
        variant={variant}
      >
        {showText && "descripted.ai"}
      </LogoIcon>
      {showText && showTagline && (
        <p className={cn(taglineSize, "mt-0.5 leading-tight max-w-[200px] text-center font-semibold", taglineClass)}>
          Brand specific AI-agents for product descriptions.
        </p>
      )}
    </div>
  );
};
